import type { FetchInvitationResult, PublicInvitation } from "@/lib/types";
import { mockInvitations } from "./mockInvitations";

/**
 * Gateway de invitaciones.
 *
 * Por defecto usa la API real servida por las Cloudflare Pages Functions en
 * `/functions` (`GET /api/invitations/:slug`). Si `USE_MOCK` está activo,
 * sirve datos mock locales (útil para desarrollar la UI sin backend).
 *
 * El contrato es async desde el principio, así el resto de la app trabaja
 * igual con mock que con red real.
 */

const USE_MOCK = import.meta.env.VITE_USE_MOCKS === "true";

/**
 * Base URL de la API pública. En Pages Functions la ruta es relativa al
 * propio dominio (`/api/invitations/:slug`), así que por defecto cadena
 * vacía. Si la API viviera en otro dominio, se define vía
 * `VITE_API_BASE_URL` (sin hardcodear nada en el repo).
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Error de red/API lanzado por `fetchInvitation`. Mensaje listo para UI. */
export class InvitationApiError extends Error {
  /** `true` cuando la causa es un 404 — para distinguirlo de un fallo real. */
  readonly notFound: boolean;
  constructor(message: string, notFound = false) {
    super(message);
    this.name = "InvitationApiError";
    this.notFound = notFound;
  }
}

/**
 * Obtiene una invitación pública por su slug.
 *
 * @returns la invitación, o `null` si no existe (404).
 * @throws {InvitationApiError} si hay un fallo de red o el servidor responde
 *   con un error distinto de 404. `error.notFound` indica si fue un 404.
 */
export async function fetchInvitation(
  slug: string
): Promise<FetchInvitationResult> {
  if (USE_MOCK) {
    return mockFetch(slug);
  }
  return apiFetch(slug);
}

//MARK: - Mock

async function mockFetch(slug: string): Promise<FetchInvitationResult> {
  // Simula latencia de red para que los estados de carga sean reales.
  await delay(120);
  return normalize(mockInvitations[slug] ?? null);
}

//MARK: - API real

async function apiFetch(slug: string): Promise<FetchInvitationResult> {
  let res: Response;
  try {
    res = await fetch(
      `${API_BASE}/api/invitations/${encodeURIComponent(slug)}`,
      { headers: { accept: "application/json" } }
    );
  } catch {
    // fetch solo lanza por fallo de red (offline, DNS, CORS). No hay respuesta.
    throw new InvitationApiError(
      "We couldn’t reach the invitation. Check your connection and try again."
    );
  }

  if (res.status === 404) return null;
  if (!res.ok) {
    throw new InvitationApiError(
      `This invitation couldn’t be loaded (${res.status}).`
    );
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new InvitationApiError("The invitation data was unreadable.");
  }
  return normalize(data as PublicInvitation | null);
}

//MARK: - utils

/**
 * Normaliza la invitación recibida (mock o API) para que la UI sea robusta.
 *
 * El contrato marca `photos` como array, pero la API puede omitirlo cuando la
 * invitación no tiene media; aquí garantizamos que siempre sea un array
 * válido antes de tocar `.length` en el componente.
 */
function normalize(inv: PublicInvitation | null): FetchInvitationResult {
  if (!inv) return null;
  return {
    ...inv,
    theme: normalizeTheme(inv.theme),
    animationMode: normalizeAnimationMode(inv.animationMode),
    ctaYesText: normalizeText(inv.ctaYesText, "Yes"),
    ctaNoText: normalizeText(inv.ctaNoText, "No"),
    photos: Array.isArray(inv.photos) ? inv.photos : [],
    media: Array.isArray(inv.media) ? inv.media : [],
    customFields: Array.isArray(inv.customFields) ? inv.customFields : []
  };
}

const THEMES = new Set<PublicInvitation["theme"]>([
  "apologyLetter",
  "classic",
  "festive",
  "elegant",
  "minimal",
  "romantic",
  "playful",
  "cosmic",
  "luxuryDark",
  "cinematic",
  "funnyChaos"
]);

const ANIMATION_MODES = new Set<PublicInvitation["animationMode"]>([
  "escapeNo",
  "growYes",
  "shakeNo",
  "swapButtons",
  "lockedNo",
  "countdownReveal",
  "confettiYes",
  "typewriterReveal",
  "envelopeReveal",
  "dramaticReveal"
]);

function normalizeTheme(theme: string): PublicInvitation["theme"] {
  return THEMES.has(theme as PublicInvitation["theme"]) ? (theme as PublicInvitation["theme"]) : "romantic";
}

function normalizeAnimationMode(animationMode: string): PublicInvitation["animationMode"] {
  if (animationMode === "runawayNo") return "escapeNo";
  if (animationMode === "tinyNo") return "countdownReveal";
  if (animationMode === "envelopeOpen") return "envelopeReveal";
  return ANIMATION_MODES.has(animationMode as PublicInvitation["animationMode"])
    ? (animationMode as PublicInvitation["animationMode"])
    : "typewriterReveal";
}

function normalizeText(value: string | undefined, fallback: string): string {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : fallback;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
